import { FormArray, ValidatorFn } from '@angular/forms';
import { saveAs } from 'file-saver';
import { TreeNode } from 'primeng/api';
import { ProductCategoryItem } from 'src/app/api/models';
import { enumTicketType } from '../shared/enums/ticket_type.enum';

export class ProductUtils {
  public static fieldTypes = [
    { label: 'Testo', value: 1 },
    { label: 'Numero intero', value: 2 },
    { label: 'Numero reale', value: 3 },
    { label: 'Data', value: 4 },
    { label: 'Data e ora', value: 5 },
    { label: 'Intervallo di date', value: 6 },
    { label: 'Testo libero', value: 7 },
    { label: 'Sì / No', value: 8 },
    { label: 'Valori fissi', value: 9 },
  ];

  public static widthUnit = ['mm', 'cm', 'm', 'km'];
  public static weightUnit = ['mg', 'Kg', 'Q', 'T'];

  public static findCategory(
    c: ProductCategoryItem[],
    idToFind: number
  ): ProductCategoryItem | void {
    for (const node of c) {
      if (node.id === idToFind) return node;
      if (node.children) {
        const child = this.findCategory(node.children, idToFind);
        if (child) return child;
      }
    }
  }

  public static catsToTreeNode(cats: ProductCategoryItem[]): TreeNode<ProductCategoryItem>[] {
    const r: TreeNode<ProductCategoryItem>[] = [];
    for (let x of cats) {
      r.push({
        label: x.name,
        key: `${x.id}`,
        children: x.children ? this.catsToTreeNode(x.children) : [],
        data: x,
      });
    }
    return r;
    // return cats.map(x => ({
    //     label: x.name,
    //     key: x.id,
    //     children: x.children ? this.catsToTreeNode(x.children) : [],
    //     data: x
    // })) as TreeNode<ProductCategoryItem>[]
  }

  public static isGroup(o: ProductFieldItem) {
    return (o as ProductFieldGroup).children !== undefined;
  }

  public static asGroup(o: ProductFieldItem) {
    return o as ProductFieldGroup;
  }

  public static asField(o: ProductFieldItem) {
    return o as ProductField;
  }
}

export interface ProductFieldItem {
  name: string;
}

export interface ProductField extends ProductFieldItem {
  type: number;
  required: boolean;
  // isGroup: boolean;
  // children: ProductField[];
  enumValues?: string[];
  enumMultiple?: boolean;
}

export interface ProductFieldGroup extends ProductFieldItem {
  children: ProductField[];
}

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const AtLeastOneSerial: ValidatorFn = c => {
  const formArray = c as FormArray;

  let result = false;

  for (let i = 0; i < formArray.length; i++) {
    const serialized = formArray.get([i, 'serialized']);
    if (serialized?.value) {
      const serials = formArray.get([i, 'serial_numbers']) as FormArray;
      result = result || serials.length > 0;
    } else {
      const qty = formArray.get([i, 'qty']);
      result = result || qty?.value > 0;
    }
  }

  if (!result) {
    return {
      atLeastOneSerial:
        'Deve essere valorizzato almeno un serial number per i prodotti della consegna.',
    };
  }

  return null;
};
/**
 * Estrae il messaggio d'errore del BE da un HttpErrorResponse, indipendentemente
 * dal fatto che `err.error` sia già un oggetto (responseType 'json') o una
 * stringa JSON non ancora parsata (responseType 'text', o corpo restituito da
 * un proxy/gateway intermedio con un content-type diverso).
 */
/**
 * Dettaglio da mostrare per un errore di salvataggio: il motivo indicato dal BE per gli errori
 * "di business" (4xx con messaggio testuale, ad esempio i motivi per cui una chiusura è rifiutata),
 * altrimenti il testo generico. Aggiunge sempre gli errori di validazione per campo, se presenti.
 */
export const saveErrorDetail = (err: any, fallback: string): string => {
  const body = parseErrorBody(err);
  const validation =
    Array.isArray(body?.validationMessages) && body.validationMessages.length > 0
      ? '\n' + (body.validationMessages as string[]).join('\n')
      : '';
  const message = body?.message;
  const isBusinessError =
    err?.status >= 400 &&
    err?.status < 500 &&
    typeof message === 'string' &&
    !!message &&
    message !== 'Si è verificato un errore.';
  return (isBusinessError ? message : fallback) + validation;
};

export const extractErrorMessage = (err: any, fallback: string): string => {
  const parsed = parseErrorBody(err);
  const message = parsed?.message;
  return typeof message === 'string' && message ? message : fallback;
};

/**
 * Estrae il corpo dell'errore del BE da un HttpErrorResponse come oggetto,
 * indipendentemente dal fatto che `err.error` sia già un oggetto (responseType
 * 'json') o una stringa JSON non ancora parsata (responseType 'text'). Utile
 * quando, oltre al `.message` testuale, serve leggere altri campi del corpo
 * (es. un `.message` strutturato con un `code` per gestire un caso speciale).
 */
export const parseErrorBody = (err: any): any => {
  const raw = err?.error;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

/**
 * Rende un nome file sicuro per il download: sostituisce i caratteri che i
 * browser non accettano in un nome file ("/" "\" ":" "*" "?" ecc.). Chrome ed
 * Edge li correggono in silenzio, Safari invece ignora l'attributo `download`
 * e apre il blob come pagina ("Safari non può aprire la pagina").
 */
export const sanitizeFileName = (name: string, fallback = 'file'): string => {
  // eslint-disable-next-line no-control-regex
  const clean = (name ?? '').replace(/[\\/:*?"<>|\u0000-\u001f]+/g, '_').trim();
  return clean || fallback;
};

/**
 * Legge il nome file dall'header Content-Disposition (con o senza virgolette),
 * altrimenti usa `fallback`. Il risultato va comunque passato a `downloadFile`
 * (o a `sanitizeFileName`) prima di essere usato.
 */
export const fileNameFromHeaders = (
  headers: { get(name: string): string | null },
  fallback: string
): string => {
  const match = /filename="?([^";]+)"?/i.exec(headers.get('content-disposition') ?? '');
  return match?.[1]?.trim() || fallback;
};

/** Scarica un blob con un nome file sanificato (vedi `sanitizeFileName`). */
export const downloadFile = (blob: Blob, fileName: string) =>
  saveAs(blob, sanitizeFileName(fileName));

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    } catch (e) {
      reject(e);
    }
  });
